import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { UserCircle, Phone, Building2, Shield } from "lucide-react";

import api from "../../services/api";
import { endpoints } from "../../services/endpoints";
import { getUserDisplayName, getUserIdOrEmail } from "../../utils/user";
import { pickRole } from "../../utils/format";

export default function DashboardUsersAdmin() {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);

  // Filtros
  const [identFilter, setIdentFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState(null);
  const [roleFilter, setRoleFilter] = useState(null);

  useEffect(() => {
    (async () => {
      await Promise.all([fetchUsers(), fetchCompanies()]);
    })();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get(endpoints.users.list);
      const list = res.data?.data ?? res.data ?? [];
      const normalized = (Array.isArray(list) ? list : []).map((u) => ({
        id: u.id,
        name: getUserDisplayName(u),
        email: u.email ?? null,
        identification: getUserIdOrEmail(u) || "",
        phone: u.phone ?? "",
        role: pickRole(u), // ADMIN | SUPERVISOR | OPERATOR | …
        active: u.active ?? true,
        companyId: u.companyId ?? u.company?.id ?? null,
      }));
      setUsers(normalized);
    } catch (err) {
      console.error(err);
      alert("Error al obtener usuarios");
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.get(endpoints.companies.list);
      const list = res.data?.data ?? res.data ?? [];
      const formatted = (Array.isArray(list) ? list : []).map((c) => ({
        value: c.id,
        label: c.name,
      }));
      setCompanies(formatted);
    } catch (err) {
      console.error(err);
      alert("Error al obtener compañías");
    }
  };

  const companyMap = useMemo(() => {
    const map = {};
    for (const c of companies) map[c.value] = c.label;
    return map;
  }, [companies]);

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const byIdent =
        identFilter.trim() === "" ||
        (u.identification || "")
          .toLowerCase()
          .includes(identFilter.trim().toLowerCase());

      const byPhone =
        phoneFilter.trim() === "" ||
        (u.phone || "")
          .toLowerCase()
          .includes(phoneFilter.trim().toLowerCase());

      const byCompany = !companyFilter || u.companyId === companyFilter.value;
      const byRole = !roleFilter || u.role === roleFilter.value;

      return byIdent && byPhone && byCompany && byRole;
    });
  }, [users, identFilter, phoneFilter, companyFilter, roleFilter]);

  const clearFilters = () => {
    setIdentFilter("");
    setPhoneFilter("");
    setCompanyFilter(null);
    setRoleFilter(null);
  };

  const toggleActive = async (user) => {
    try {
      const newActive = !user.active;

      // Preferente: endpoint dedicado de estado (si existe)
      try {
        await api.patch(endpoints.users.updateStatus(user.id), { active: newActive });
      } catch {
        // Fallback a update genérico
        await api.patch(endpoints.users.update(user.id), { active: newActive });
      }

      await fetchUsers();
    } catch (err) {
      console.error(err);
      alert("No se pudo actualizar el estado del usuario");
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Usuarios (todas las compañías)</h1>
        <button
          onClick={() => navigate("/createuseradmin/")}
          className="bg-blue-600 text-white px-4 py-2 rounded"
        >
          Crear Usuario
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded shadow space-y-4">
        <h2 className="text-lg font-medium">Filtros</h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block font-medium">Identificación</label>
            <input
              type="text"
              value={identFilter}
              onChange={(e) => setIdentFilter(e.target.value)}
              placeholder="Buscar por identificación"
              className="border rounded w-full p-2"
            />
          </div>

        <div>
            <label className="block font-medium">Teléfono</label>
            <input
              type="text"
              value={phoneFilter}
              onChange={(e) => setPhoneFilter(e.target.value)}
              placeholder="Buscar por teléfono"
              className="border rounded w-full p-2"
            />
          </div>

          <div>
            <label className="block font-medium">Compañía</label>
            <Select
              options={companies}
              value={companyFilter}
              onChange={setCompanyFilter}
              isClearable
              placeholder="Selecciona compañía"
            />
          </div>

          <div>
            <label className="block font-medium">Rol</label>
            <Select
              options={[
                { value: "ADMIN", label: "ADMIN" },
                { value: "SUPERVISOR", label: "SUPERVISOR" },
                { value: "OPERARIO", label: "OPERARIO" },
              ]}
              value={roleFilter}
              onChange={setRoleFilter}
              isClearable
              placeholder="Selecciona rol"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={clearFilters}
            className="px-3 py-2 rounded border"
            type="button"
          >
            Limpiar filtros
          </button>
          <div className="text-sm text-gray-500 self-center">
            {filteredUsers.length} de {users.length} resultados
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border rounded shadow">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left">Nombre</th>
              <th className="px-4 py-2 text-left">Identificación</th>
              <th className="px-4 py-2 text-left">Teléfono</th>
              <th className="px-4 py-2 text-left">Rol</th>
              <th className="px-4 py-2 text-left">Compañía</th>
              <th className="px-4 py-2 text-left">Estado</th>
              <th className="px-4 py-2 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-2 flex items-center gap-2">
                  <UserCircle size={18} className="text-gray-500" />
                  {user.name}
                </td>
                <td className="px-4 py-2">{user.identification}</td>
                <td className="px-4 py-2 flex items-center gap-2">
                  <Phone size={16} className="text-blue-500" />
                  {user.phone || <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-2">
                  <span className="inline-flex items-center gap-1">
                    <Shield size={16} className="text-purple-500" />
                    {user.role}
                  </span>
                </td>
                <td className="px-4 py-2 flex items-center gap-2">
                  <Building2 size={16} className="text-emerald-600" />
                  {companyMap[user.companyId] || <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-2">
                  {user.active ? (
                    <span className="text-green-600">Activo</span>
                  ) : (
                    <span className="text-red-600">Inactivo</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/usersadmin/${user.id}/edit`)}
                      className="px-3 py-1 rounded border text-blue-600 hover:bg-blue-50"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => toggleActive(user)}
                      className={`px-3 py-1 rounded border ${
                        user.active
                          ? "text-red-600 hover:bg-red-50"
                          : "text-green-600 hover:bg-green-50"
                      }`}
                    >
                      {user.active ? "Inactivar" : "Activar"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {filteredUsers.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-gray-500" colSpan={7}>
                  No hay usuarios que coincidan con los filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}